import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

/**
 * Venue-scoped presellable products (ADR 0029). A Pass carries a `type` discriminator, an
 * auto-generated `code`, a name/description, its room coverage (`allRooms` or an explicit
 * `roomIds` set), and one or more priced variations. `amount` rides as a decimal string so the
 * scale the user typed reaches the backend `Money` VO unrounded; the currency is the Venue's,
 * applied server-side. Create, edit, lifecycle, and room coverage all flow through here.
 */
export type PassType = 'STANDARD' | 'SESSION' | 'RECURRING';

export const PASS_TYPES: PassType[] = ['STANDARD', 'SESSION', 'RECURRING'];

export type DepositType = 'PERCENTAGE' | 'FIXED';

/**
 * The deposit (partial-payment) policy on a pass (PRD #164 stories 18-20). When not allowed it
 * carries no type or value. When allowed, `value` rides as a decimal string — a PERCENTAGE is a
 * plain number, a FIXED amount is in the Venue's currency (applied server-side); the response
 * adds the `currency` for a FIXED amount.
 */
export interface DepositInput {
  allowed: boolean;
  type?: DepositType;
  value?: string;
}

export interface Deposit {
  allowed: boolean;
  type: DepositType | null;
  value: string | null;
  currency: string | null;
}

export interface PassVariation {
  id: string;
  name: string;
  amount: string;
  currency: string;
  requiresWaiver: boolean;
  active: boolean;
}

/** ISO day names, Monday-first. The backend packs these to a weekday bitmask; an empty list = every day. */
export type Weekday =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export const WEEKDAYS: Weekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

/**
 * A weekday-and-date window (sales availability). `weekdays` empty means every day; `startDate` /
 * `endDate` are ISO dates (`yyyy-MM-dd`) or null for open-ended.
 */
export interface AvailabilityWindow {
  weekdays: Weekday[];
  startDate: string | null;
  endDate: string | null;
}

/** When a pass may be sold/used: a `validDays` window and an `exclusionDays` window (exclusions win). */
export interface SalesAvailability {
  validDays: AvailabilityWindow;
  exclusionDays: AvailabilityWindow;
}

export interface AvailabilityWindowInput {
  weekdays: Weekday[];
  startDate?: string;
  endDate?: string;
}

export interface SalesAvailabilityInput {
  validDays: AvailabilityWindowInput;
  exclusionDays: AvailabilityWindowInput;
}

/** The booking-reminder policy: when enabled, `daysBefore` is how many days ahead it is sent. */
export interface BookingReminder {
  enabled: boolean;
  daysBefore: number | null;
}

export interface BookingReminderInput {
  enabled: boolean;
  daysBefore?: number;
}

export interface Pass {
  id: string;
  venueId: string;
  type: PassType;
  code: string;
  name: string;
  description: string | null;
  allRooms: boolean;
  roomIds: string[];
  waiverRequired: boolean;
  deposit: Deposit;
  sessionDurationMinutes: number | null;
  salesAvailability: SalesAvailability;
  hideFromOnlineSale: boolean;
  bookingReminder: BookingReminder;
  confirmationRequired: boolean;
  active: boolean;
  variations: PassVariation[];
  createdAt: string;
  updatedAt: string;
}

export interface PassList {
  items: Pass[];
}

export interface CreatePassInput {
  type: PassType;
  name: string;
  description?: string;
  allRooms: boolean;
  roomIds: string[];
  deposit: DepositInput;
  /** Required (30/60/90/120) for a SESSION pass; null/omitted for other types. */
  sessionDurationMinutes?: number;
  salesAvailability?: SalesAvailabilityInput;
  hideFromOnlineSale: boolean;
  bookingReminder: BookingReminderInput;
  confirmationRequired: boolean;
  variations: { name: string; amount: string; requiresWaiver: boolean }[];
}

/**
 * The composite edit (PRD #164 story 30): one call carrying the pass identity and the whole
 * desired variation set. A variation with an `id` is an existing one to rename/reprice/(de)activate;
 * one without an `id` is added. The backend reconciles the set atomically and enforces the
 * last-active-variation block.
 */
export interface EditVariationInput {
  id?: string;
  name: string;
  amount: string;
  requiresWaiver: boolean;
  active: boolean;
}

export interface EditPassInput {
  name: string;
  description?: string;
  allRooms: boolean;
  roomIds: string[];
  deposit: DepositInput;
  sessionDurationMinutes?: number;
  salesAvailability?: SalesAvailabilityInput;
  hideFromOnlineSale: boolean;
  bookingReminder: BookingReminderInput;
  confirmationRequired: boolean;
  variations: EditVariationInput[];
}

const listKey = (venueId: string, type?: PassType) => ['passes', venueId, type ?? 'ALL'] as const;

export const getPasses = (venueId: string, type?: PassType): Promise<PassList> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/passes', {
      params: { path: { venueId }, query: type ? { type } : undefined },
    }),
  ) as Promise<PassList>;

export const createPass = (venueId: string, input: CreatePassInput): Promise<Pass> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/passes', {
      params: { path: { venueId } },
      body: input,
    }),
  ) as Promise<Pass>;

export const editPass = (venueId: string, passId: string, input: EditPassInput): Promise<Pass> =>
  unwrap(
    apiClient.PUT('/api/v1/venue-workspace/venues/{venueId}/passes/{passId}', {
      params: { path: { venueId, passId } },
      body: input,
    }),
  ) as Promise<Pass>;

export const setPassActive = (venueId: string, passId: string, active: boolean): Promise<Pass> =>
  unwrap(
    apiClient.PATCH('/api/v1/venue-workspace/venues/{venueId}/passes/{passId}/active', {
      params: { path: { venueId, passId } },
      body: { active },
    }),
  ) as Promise<Pass>;

export function usePass(venueId: string, passId: string, enabled = true) {
  return useQuery({
    queryKey: ['passes', venueId, 'detail', passId],
    queryFn: async () => {
      const list = await getPasses(venueId);
      return list.items.find((pass) => pass.id === passId) ?? null;
    },
    enabled: enabled && !!venueId && !!passId,
  });
}

export function usePasses(venueId: string, type: PassType | undefined, enabled = true) {
  return useQuery({
    queryKey: listKey(venueId, type),
    queryFn: () => getPasses(venueId, type),
    enabled: enabled && !!venueId,
  });
}

export function useCreatePass(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePassInput) => createPass(venueId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passes', venueId] }),
  });
}

export function useEditPass(venueId: string, passId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EditPassInput) => editPass(venueId, passId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passes', venueId] }),
  });
}

export function useSetPassActive(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ passId, active }: { passId: string; active: boolean }) =>
      setPassActive(venueId, passId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passes', venueId] }),
  });
}
